import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchService } from '../../services/searchService';
import { MaterialSymbol } from '../common/MaterialSymbol';

const RECENT_KEY = 'oa_recent_search';
const RECENT_MAX = 8;

export interface PaletteEntry {
    label: string;
    /** Material-Symbols-Name für das Icon links */
    icon: string;
    /** Typ-Label rechts (z. B. "Seite", "Suche") */
    type: string;
    /** Router-Ziel beim Auswählen */
    to: string;
}

interface CommandPaletteProps {
    open: boolean;
    onClose: () => void;
    /** Rollenabhängige Seiten-Einträge für den Schnellzugriff (kommen aus Layout) */
    pages: PaletteEntry[];
}

const readRecent = (): string[] => {
    try {
        const parsed = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
        return Array.isArray(parsed) ? parsed.filter((r) => typeof r === 'string') : [];
    } catch {
        return [];
    }
};

const highlight = (text: string, q: string): React.ReactNode => {
    if (!q) return text;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return text;
    return (
        <>
            {text.slice(0, i)}
            <mark>{text.slice(i, i + q.length)}</mark>
            {text.slice(i + q.length)}
        </>
    );
};

/**
 * Globale Suche als Command-Palette (Design-System "Suche (Command Palette)"):
 * ⌘/Strg+K-Overlay über der App-Bar, gruppierte Treffer mit Highlight,
 * zuletzt gesuchte Begriffe, Seiten-Schnellzugriff, Tastatur-Navigation.
 * Schließen per X-Button (Kunden-Feedback), Scrim-Klick oder Esc.
 */
export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose, pages }) => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [recent, setRecent] = useState<string[]>([]);
    const [active, setActive] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (open) {
            setQuery('');
            setSuggestions([]);
            setActive(-1);
            setRecent(readRecent());
            const t = setTimeout(() => inputRef.current?.focus(), 60);
            return () => clearTimeout(t);
        }
    }, [open]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (query.trim().length < 2) {
            setSuggestions([]);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            try {
                const results = await searchService.getSuggestions(query.trim(), 8);
                setSuggestions(results);
            } catch {
                setSuggestions([]);
            }
        }, 250);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query]);

    const rememberSearch = (term: string) => {
        const next = [term, ...readRecent().filter((r) => r !== term)].slice(0, RECENT_MAX);
        try {
            localStorage.setItem(RECENT_KEY, JSON.stringify(next));
        } catch {
            /* storage voll/gesperrt — Verlauf ist nice-to-have */
        }
    };

    const q = query.trim();
    const filteredPages = q
        ? pages.filter((p) => p.label.toLowerCase().includes(q.toLowerCase()))
        : pages.slice(0, 5);

    // Sichtbare, per Pfeiltasten ansteuerbare Einträge in Anzeige-Reihenfolge
    const items: PaletteEntry[] = [
        ...suggestions.map((s) => ({ label: s, icon: 'search', type: 'Suche', to: `/search?q=${encodeURIComponent(s)}` })),
        ...filteredPages,
    ];

    const choose = useCallback(
        (entry: PaletteEntry) => {
            if (entry.type === 'Suche') rememberSearch(entry.label);
            onClose();
            navigate(entry.to);
        },
        [navigate, onClose],
    );

    const submitFreeText = () => {
        if (q.length < 2) return;
        rememberSearch(q);
        onClose();
        navigate(`/search?q=${encodeURIComponent(q)}`);
    };

    const scrollActiveIntoView = (index: number) => {
        const el = bodyRef.current?.querySelectorAll<HTMLElement>('.pal-item')[index];
        el?.scrollIntoView({ block: 'nearest' });
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown' && items.length) {
            e.preventDefault();
            const next = (active + 1) % items.length;
            setActive(next);
            scrollActiveIntoView(next);
        } else if (e.key === 'ArrowUp' && items.length) {
            e.preventDefault();
            const next = (active - 1 + items.length) % items.length;
            setActive(next);
            scrollActiveIntoView(next);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (active >= 0 && items[active]) choose(items[active]);
            else submitFreeText();
        }
    };

    if (!open) return null;

    let itemIndex = -1;
    const renderItem = (entry: PaletteEntry) => {
        itemIndex += 1;
        const idx = itemIndex;
        return (
            <div
                key={`${entry.type}:${entry.label}`}
                className={`pal-item${idx === active ? ' active' : ''}`}
                onClick={() => choose(entry)}
                onMouseMove={() => setActive(idx)}
            >
                <span className="pico">
                    <MaterialSymbol name={entry.icon} size={18} />
                </span>
                <span className="ptt">{highlight(entry.label, q)}</span>
                <span className="ptype">{entry.type}</span>
            </div>
        );
    };

    return (
        <div className="pal-scrim open" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="palette" role="dialog" aria-modal="true" aria-label="Suche">
                <div className="pal-top">
                    <MaterialSymbol name="search" size={20} />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Kurse, Artikel, Dateien durchsuchen …"
                        autoComplete="off"
                    />
                    <button className="pal-x" type="button" aria-label="Suche schließen" onClick={onClose}>
                        <MaterialSymbol name="close" />
                    </button>
                </div>
                <div className="pal-body" ref={bodyRef}>
                    {!q && recent.length > 0 && (
                        <>
                            <div className="pal-label">Zuletzt gesucht</div>
                            <div className="pal-recent">
                                {recent.slice(0, 6).map((term) => (
                                    <button key={term} className="pal-chip" onClick={() => setQuery(term)}>
                                        <MaterialSymbol name="schedule" size={14} />
                                        <span>{term}</span>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                    {suggestions.length > 0 && <div className="pal-label">Vorschläge</div>}
                    {suggestions.map((s) => renderItem({ label: s, icon: 'search', type: 'Suche', to: `/search?q=${encodeURIComponent(s)}` }))}
                    {filteredPages.length > 0 && <div className="pal-label">{q ? 'Seiten' : 'Schnellzugriff'}</div>}
                    {filteredPages.map((p) => renderItem(p))}
                    {q.length >= 2 && items.length === 0 && (
                        <div className="pal-empty">
                            <MaterialSymbol name="search" size={38} />
                            <p>Keine Ergebnisse für „{q}“ — Enter öffnet die Volltextsuche</p>
                        </div>
                    )}
                </div>
                <div className="pal-foot">
                    <span className="k">
                        <kbd>↑</kbd>
                        <kbd>↓</kbd> Navigieren
                    </span>
                    <span className="k">
                        <kbd>↵</kbd> Öffnen
                    </span>
                    <span className="k">
                        <kbd>esc</kbd> Schließen
                    </span>
                </div>
            </div>
        </div>
    );
};

export default CommandPalette;
